import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Banknote, AlertCircle, Sparkles, ShieldCheck, Activity } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function BankMatchingSettingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchSettings = async () => {
    try {
      const r = await api.get('/bank-matching/settings');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/bank-matching/settings', {
        auto_confirm_high_confidence: !!data.auto_confirm_high_confidence,
        min_confidence_for_suggestion: data.min_confidence_for_suggestion,
        high_confidence_threshold: Number(data.high_confidence_threshold),
        medium_confidence_threshold: Number(data.medium_confidence_threshold),
      });
      setData(r.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !data) return <DashboardLayout><div className="p-8 text-center text-muted-foreground">Loading…</div></DashboardLayout>;

  const high = Number(data.high_confidence_threshold);
  const medium = Number(data.medium_confidence_threshold);
  const valid = !isNaN(high) && !isNaN(medium) && medium >= 0 && medium < high && high <= 100;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="bank-matching-settings-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Banknote size={20} className="text-violet-700 dark:text-violet-400" />
            </div>
            <div>
              <h1 className="text-2xl font-outfit font-bold">Bank Matching Settings</h1>
              <p className="text-xs text-muted-foreground">Tune the smart-match engine behaviour for your tenant.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate('/bank-match-audit')} data-testid="open-audit-log">
              <ShieldCheck size={12} className="mr-1.5" />Audit log
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/ws-playground')} data-testid="open-ws-playground">
              <Activity size={12} className="mr-1.5" />WS playground
            </Button>
          </div>
        </div>

        {/* Auto-confirm card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-outfit flex items-center gap-2"><Sparkles size={14} /> Auto-confirm matches</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
              <div className="min-w-0 pr-4">
                <Label className="text-sm font-medium">Auto-confirm HIGH-confidence matches</Label>
                <p className="text-xs text-muted-foreground mt-0.5">When ON, IBAN-exact + score≥{high}% matches are auto-posted as Confirmed without manager review.</p>
              </div>
              <Switch
                checked={!!data.auto_confirm_high_confidence}
                onCheckedChange={(v) => setData({ ...data, auto_confirm_high_confidence: v })}
                data-testid="toggle-auto-confirm"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Minimum confidence for suggestions</Label>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1">Filter what shows up in /reconciliation suggestion lists.</p>
              <Select value={data.min_confidence_for_suggestion}
                onValueChange={(v) => setData({ ...data, min_confidence_for_suggestion: v })}>
                <SelectTrigger className="h-9 max-w-xs" data-testid="select-min-confidence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low (≥{medium}%)</SelectItem>
                  <SelectItem value="medium">Medium (≥{medium}%)</SelectItem>
                  <SelectItem value="high">High only (≥{high}%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Threshold card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-outfit">Confidence thresholds</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">High threshold (≥)</Label>
                <Input type="number" min={0} max={100} step={1}
                  value={data.high_confidence_threshold}
                  onChange={(e) => setData({ ...data, high_confidence_threshold: e.target.value })}
                  className="font-mono mt-1"
                  data-testid="input-high-threshold" />
                <p className="text-[10px] text-muted-foreground mt-1">Default 90 · only HIGH-confidence rows can auto-confirm.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Medium threshold (≥)</Label>
                <Input type="number" min={0} max={100} step={1}
                  value={data.medium_confidence_threshold}
                  onChange={(e) => setData({ ...data, medium_confidence_threshold: e.target.value })}
                  className="font-mono mt-1"
                  data-testid="input-medium-threshold" />
                <p className="text-[10px] text-muted-foreground mt-1">Default 75 · below this is LOW.</p>
              </div>
            </div>

            {!valid && (
              <div className="flex items-center gap-2 p-2 rounded-md bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 text-xs" data-testid="threshold-error">
                <AlertCircle size={14} />
                <span>Required: 0 ≤ medium &lt; high ≤ 100</span>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString() : '—'}
              {data.updated_by && <> by <strong>{data.updated_by}</strong></>}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={!valid || saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-settings">
            <Save size={14} className="mr-1.5" />
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
